import { useState, type FC } from "react";
import {
  Box,
  Button,
  Collapse,
  Divider,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import type { FileWithPath } from "@mantine/dropzone";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconFolder, IconPlus } from "@tabler/icons-react";
import {
  FileType,
  type Order,
  getFileDisplayName,
  getTraceabilitiesFile,
} from "@jasper/shared";
import { FileDropzone } from "/@/components/order/FileDropzone";
import { useOrderFilesManagement } from "/@/pages/orders/useOrderFilesManagement";
import { OrderDetailCard } from "./OrderDetailCard";
import { OrderFileRow } from "./OrderFileRow";
import { PhoneUploadQrCard } from "/@/components/order/PhoneUploadQrCard";
import {
  formatDayMonth,
  getFileExtension,
  ICON_STROKE,
} from "./order-page-v2.utils";

const TOGGLED_BUTTON_STYLES = {
  root: { backgroundColor: "var(--mantine-color-brand-0)" },
  label: { color: "var(--mantine-color-brand-8)" },
  section: { color: "var(--mantine-color-brand-8)" },
};

type OrderFilesCardProps = {
  order: Order;
};

export const OrderFilesCard: FC<OrderFilesCardProps> = ({ order }) => {
  const [isAddZoneOpened, { toggle: toggleAddZone }] = useDisclosure(false);
  const [isUploading, setIsUploading] = useState(false);
  const { qrCodeCanvasRef, files, uploadFiles, deleteFile, downloadFile } =
    useOrderFilesManagement({ order });

  const traceabilityFiles = getTraceabilitiesFile(files);
  const listedFiles = files
    .filter(file => !traceabilityFiles.includes(file))
    .toSorted(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  const addFiles = async (filesToAdd: FileWithPath[]) => {
    setIsUploading(true);
    try {
      await uploadFiles(filesToAdd, FileType.ORDER_ADDON_FILE);
    } catch (error) {
      console.error(error);
      notifications.show({
        color: "danger",
        title: "Erreur lors de l'ajout des fichiers",
        message: "Les fichiers n'ont pas pu être ajoutés",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <OrderDetailCard
      icon={IconFolder}
      iconColor="ink"
      title="Fichiers"
      rightSection={
        <Button
          variant="subtle"
          size="sm"
          leftSection={<IconPlus size={14} stroke={ICON_STROKE} />}
          aria-expanded={isAddZoneOpened}
          onClick={toggleAddZone}
          styles={isAddZoneOpened ? TOGGLED_BUTTON_STYLES : undefined}
        >
          Ajouter des fichiers
        </Button>
      }
    >
      {listedFiles.length > 0 ? (
        <Stack gap="s2">
          {listedFiles.map(file => (
            <OrderFileRow
              key={file.id}
              name={getFileDisplayName(file.key)}
              extension={getFileExtension(file.key)}
              meta={`Ajouté le ${formatDayMonth(new Date(file.createdAt))}`}
              onDownload={() => downloadFile(file.key)}
              onDelete={
                file.fileType === FileType.ORDER_ADDON_FILE
                  ? () => deleteFile(file.id, file.key)
                  : undefined
              }
            />
          ))}
        </Stack>
      ) : (
        <Text size="sm">Aucun fichier pour cette commande</Text>
      )}
      <Collapse expanded={isAddZoneOpened} keepMountedMode="display-none">
        <Divider color="ink.1" my="s4" />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="s4">
          <PhoneUploadQrCard
            canvasRef={qrCodeCanvasRef}
            description="Scannez le code : photos et fichiers arrivent dans la commande."
          />
          <Box>
            <FileDropzone
              description="STL, photos, PDF · glisser-déposer"
              isLoading={isUploading}
              onDrop={addFiles}
            />
          </Box>
        </SimpleGrid>
      </Collapse>
    </OrderDetailCard>
  );
};
